import React, { FC, useState, MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AuthFormModal from './AuthFormModal';
import HerdFormModal from './HerdFormModal';
import UpdateAvatarModal from './UpdateAvatarModal';
import UserAvatar from './UserAvatar';
import storageService from '../utils/localStorage';
import { Button, Menu, MenuItem, Typography, ListItemIcon, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FilterVintageIcon from '@mui/icons-material/FilterVintage';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaletteIcon from '@mui/icons-material/Palette';
import TuneIcon from '@mui/icons-material/Tune';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import type { UserState } from '../types/common';

interface DesktopUserMenuProps {
  user?: UserState | null;
  handleLogout: () => void;
}

const DesktopUserMenu: FC<DesktopUserMenuProps> = ({ user, handleLogout }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleClose();
    handleLogout();
  };

  const loggedUser = storageService.loadUser() || user;

  return (
    <div>
      {loggedUser ? (
        <>
          <Button onClick={handleMenu} sx={{ textTransform: 'none', display: 'flex', alignItems: 'center' }}>
            {/* Rounded (not circular) and unframed: this is chrome. Both are
                carried as props rather than "fixed" during the migration. */}
            <UserAvatar
              user={loggedUser as any}
              size={32}
              variant="rounded"
              frame="none"
              sx={{ mr: 0.5, bgcolor: theme.palette.secondary.main }}
            />
            <Box>
              <Typography color="secondary">{loggedUser.username}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterVintageIcon fontSize="inherit" style={{ marginRight: theme.tokens.spacing.inlineCompact }} color="secondary" />
                <Typography variant="caption">{`${loggedUser.reactionPoints ?? 0} points`}</Typography>
              </Box>
            </Box>
          </Button>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem component={RouterLink} to={`/u/${loggedUser.username}`} onClick={handleClose}>
              <ListItemIcon>
                <AccountCircleIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> My Profile
              </ListItemIcon>
            </MenuItem>
            <MenuItem component={RouterLink} to="/shop" onClick={handleClose}>
              <ListItemIcon>
                <StorefrontIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Store
              </ListItemIcon>
            </MenuItem>
            <MenuItem component={RouterLink} to="/preferences" onClick={handleClose}>
              <ListItemIcon>
                <TuneIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Preferences
              </ListItemIcon>
            </MenuItem>
            <MenuItem component={RouterLink} to="/customize" onClick={handleClose}>
              <ListItemIcon>
                <PaletteIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Customize
              </ListItemIcon>
            </MenuItem>
            <MenuItem component={RouterLink} to="/wallet" onClick={handleClose}>
              <ListItemIcon>
                <AccountBalanceWalletIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Wallet
              </ListItemIcon>
            </MenuItem>
            <HerdFormModal type="menu" handleCloseMenu={handleClose} />
            <UpdateAvatarModal handleCloseMenu={handleClose} user={loggedUser} />
            <MenuItem onClick={handleLogoutClick}>
              <ListItemIcon>
                <PowerSettingsNewIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Logout
              </ListItemIcon>
            </MenuItem>
          </Menu>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AuthFormModal />
        </Box>
      )}
    </div>
  );
};

export default DesktopUserMenu;
